/**
 * useSubscriptionStatus Hook
 *
 * React hook for accessing subscription status in components.
 * Replaces the old synchronous Cognito attribute checks with async API-based checks.
 */

import { useState, useEffect, useContext } from 'react';
import { getSubscriptionStatusSummary } from '../services/subscription/subscriptionStatusService';
import { isTenantContractBased } from '../services/tenant/tenantService';
import { CognitoUserContext } from '../contexts/cognitoUserContext';

/**
 * Hook to get subscription status
 * @param {object} options - Hook options
 * @param {boolean} options.autoLoad - Whether to automatically load on mount (default: true)
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: null - no refresh)
 * @returns {object} Subscription status and control functions
 */
export function useSubscriptionStatus({ autoLoad = true, refreshInterval = null } = {}) {
  const { cognitoUser } = useContext(CognitoUserContext);
  const [status, setStatus] = useState({
    hasSubscription: false,
    isActive: false,
    isCancelling: false,
    status: null,
    periodEnd: null,
    cancelAtPeriodEnd: false,
    isContractBased: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if tenant is contract-based (bypasses subscription)
      const isContract = isTenantContractBased(cognitoUser);
      if (isContract) {
        setStatus({
          hasSubscription: true,
          isActive: true,
          isCancelling: false,
          status: 'contract',
          periodEnd: null,
          cancelAtPeriodEnd: false,
          isContractBased: true
        });
        return;
      }

      // Get subscription status from API
      const summary = await getSubscriptionStatusSummary();
      setStatus({
        ...summary,
        isContractBased: false
      });
    } catch (err) {
      console.error('[useSubscriptionStatus] Error loading subscription status:', err);
      setError(err);
      setStatus({
        hasSubscription: false,
        isActive: false,
        isCancelling: false,
        status: null,
        periodEnd: null,
        cancelAtPeriodEnd: false,
        isContractBased: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad && cognitoUser) {
      loadStatus();
    }
  }, [autoLoad, cognitoUser?.username]); // Reload if user changes

  // Auto-refresh at interval
  useEffect(() => {
    if (refreshInterval && refreshInterval > 0) {
      const intervalId = setInterval(loadStatus, refreshInterval);
      return () => clearInterval(intervalId);
    }
  }, [refreshInterval]);

  return {
    // Status flags
    ...status,

    // Loading state
    loading,
    error,

    // Control functions
    refresh: loadStatus,

    // Convenience flags
    canSubscribe: !status.isActive && !status.isContractBased,
    shouldShowCancelButton: status.isActive && !status.cancelAtPeriodEnd,
    shouldShowReactivateButton: status.isCancelling,
    userHasAccess: status.isActive || status.isContractBased
  };
}

/**
 * Simple hook that just returns whether user has active subscription
 * Useful for quick access control checks
 * @returns {boolean} True if user has active subscription or is contract-based
 */
export function useHasActiveSubscription() {
  const { userHasAccess, loading } = useSubscriptionStatus();
  return !loading && userHasAccess;
}
